"use client";

import {useState, type ChangeEvent, type FormEvent} from "react";
import {useTranslations} from "next-intl";
import {UserJwtService} from "@/services/userJwt-service";
import {CreateContactUsDto} from "@/marketing-api-client";
import {MarketingService} from "@/services/marketing-service";

type ContactFormProps = {
    isRtl: boolean;
};

type FormData = {
    fullName: string;
    email: string;
    contactNo: string;
    subject: string;
    content: string;
};

const initialFormState: FormData = {
    fullName: "",
    email: "",
    contactNo: "",
    subject: "",
    content: "",
};

export default function ContactForm({isRtl}: ContactFormProps) {
    const tPage = useTranslations('page.contactus');
    const [formData, setFormData] = useState<FormData>(initialFormState);


    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const apiBaseUrl = process.env.NEXT_PUBLIC_MARKETING_API_BASE_URL;
    const apiUsername = process.env.NEXT_PUBLIC_MARKETING_API_USERNAME;
    const apiPassword = process.env.NEXT_PUBLIC_MARKETING_API_PASSWORD;

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value} = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};
        if (!formData.fullName.trim()) newErrors.fullName = tPage('contactForm.errorRequired');
        if (!formData.subject.trim()) newErrors.subject = tPage('contactForm.errorRequired');
        if (!formData.contactNo.trim()) {
            newErrors.contactNo = tPage('contactForm.errorRequired');
        } else if (!/^[\d+\-\s()]{8,}$/.test(formData.contactNo.trim())) {
            newErrors.contactNo = tPage('contactForm.errorContactNo');
        }

        if (!formData.content.trim()) newErrors.content = tPage('contactForm.errorRequired');

        if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = tPage('contactForm.errorEmail');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
         event.preventDefault();
         setSubmitted(true);
         setTimeout(() => {
             setSubmitted(false);
             setFormData(initialFormState);
         }, 3000);
     };*/

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setStatus(null);
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const userJwtService = new UserJwtService(apiBaseUrl ?? '');
            const jwtResponse = await userJwtService.authenticateUser({
                username: apiUsername,
                password: apiPassword
            })


            const createContactUsDto: CreateContactUsDto = {
                ...formData,
            };

            const marketingService = new MarketingService(apiBaseUrl ?? '', jwtResponse.accessToken ?? '');
            await marketingService.createContactUs(createContactUsDto);


            setStatus({type: 'success', message: tPage('contactForm.successMsg')});
            setFormData({fullName: '', email: '', contactNo: '', content: '', subject: ''});
            setErrors({});
        } catch (error) {
            console.error('Submission error:', error);
            setStatus({type: 'error', message: tPage('contactForm.errorMessage')});
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <>

            <div>
                {status && (
                    <div
                        className={`mb-6 border rounded-lg p-4 ${status.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                        {status.message}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="bg-white rounded-lg">
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 ${isRtl ? "flex-row-reverse" : ""}`}>
                        <div>
                            <label htmlFor="fullName"
                                   className={`block text-gray-700 font-bold mb-2 ${isRtl ? "text-right" : ""}`}>
                                {tPage("contactForm.nameLabel")}
                            </label>
                            <input
                                id="fullName"
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${isRtl ? "text-right" : ""
                                } ${errors.fullName ? "border-red-500" : ""}`}
                                placeholder={tPage("contactForm.namePlaceHolder")}
                            />
                            {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                        </div>
                        <div>
                            <label htmlFor="email"
                                   className={`block text-gray-700 font-bold mb-2 ${isRtl ? "text-right" : ""}`}>
                                {tPage("contactForm.emailLabel")}
                            </label>
                            <input
                                id="email"
                                type="text"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${isRtl ? "text-right" : ""
                                } ${errors.email ? "border-red-500" : ""}`}
                                placeholder={tPage("contactForm.emailPlaceHolder")}
                            />
                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                        </div>
                    </div>

                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 ${isRtl ? "flex-row-reverse" : ""}`}>
                        <div>
                            <label htmlFor="contactNo"
                                   className={`block text-gray-700 font-bold mb-2 ${isRtl ? "text-right" : ""}`}>
                                {tPage("contactForm.contactNoLabel")}
                            </label>
                            <input
                                id="contactNo"
                                type="tel"
                                name="contactNo"
                                value={formData.contactNo}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${isRtl ? "text-right" : ""
                                } ${errors.contactNo ? "border-red-500" : ""}`}
                                placeholder={tPage("contactForm.contactNoPlaceHolder")}
                            />
                            {errors.contactNo && <p className="text-red-500 text-sm mt-1">{errors.contactNo}</p>}
                        </div>
                        <div>
                            <label htmlFor="subject"
                                   className={`block text-gray-700 font-bold mb-2 ${isRtl ? "text-right" : ""}`}>
                                {tPage("contactForm.subjectLabel")}
                            </label>
                            <input
                                id="subject"
                                type="text"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${isRtl ? "text-right" : ""
                                } ${errors.subject ? "border-red-500" : ""}`}
                                placeholder={tPage("contactForm.subjectPlaceHolder")}
                            />
                            {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                        </div>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="content"
                               className={`block text-gray-700 font-bold mb-2 ${isRtl ? "text-right" : ""}`}>
                            {tPage("contactForm.messageLabel")}
                        </label>
                        <textarea
                            id="content"
                            name="content"
                            value={formData.content}
                            onChange={handleChange}
                            rows={5}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg resize-none ${isRtl ? "text-right" : ""
                            } ${errors.content ? "border-red-500" : ""}`}
                            placeholder={tPage("contactForm.messagePlaceHolder")}
                        />
                        {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content}</p>}
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`sm-btn w-full justify-center ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                        {isLoading ? "..." : tPage("contactForm.submitButton")}
                    </button>
                </form>
            </div>
        </>

    );
}



